import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useAppServices } from "../../../hook/services";
import ReactApexChart from "react-apexcharts";

const isActiveLocation = (l) =>
  l.saasMode === "active" && l.subscriptionStatus === "active";

const countInRange = (data, start, end, dateField, predicate = () => true) => {
  if (!start) return data.filter(predicate).length;
  return data.filter((item) => {
    if (!item[dateField] || !predicate(item)) return false;
    const d = new Date(item[dateField]);
    return !isNaN(d) && d >= start && d <= end;
  }).length;
};

const computeDelta = (curr, prev) => {
  if (prev === 0 && curr === 0) return { value: 0, dir: "flat" };
  if (prev === 0) return { value: 100, dir: "up" };
  const change = ((curr - prev) / prev) * 100;
  return {
    value: Math.abs(Math.round(change)),
    dir: change > 0.5 ? "up" : change < -0.5 ? "down" : "flat",
  };
};

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return "—";
  return dt.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  return (
    ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?"
  );
};

const buildUserName = (u) => {
  if (!u) return "Unknown";
  const fl = `${u.firstName || ""} ${u.lastName || ""}`.trim();
  return fl || u.username || u.name || u.email || "Unknown";
};

const ticketStatusTone = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "resolved" || s === "closed" || s === "completed") return "bp-success";
  if (s === "in progress" || s === "in-progress" || s === "processing") return "bp-info";
  if (s === "cancelled" || s === "canceled") return "bp-danger";
  return "bp-warning";
};

const priorityTone = (priority) => {
  const p = (priority || "").toLowerCase();
  if (p === "high" || p === "urgent") return "bp-danger";
  if (p === "low") return "bp-muted";
  return "bp-warning";
};

const subscriptionTone = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "active") return "bp-success";
  if (s === "trial" || s === "trialing") return "bp-warning";
  if (s === "cancelled" || s === "canceled") return "bp-danger";
  return "bp-muted";
};

const initiatorTone = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "completed" || s === "success" || s === "active") return "bp-success";
  if (s === "pending" || s === "in progress" || s === "in-progress") return "bp-warning";
  if (s === "failed" || s === "error") return "bp-danger";
  return "bp-muted";
};

const EmptyState = ({ icon, title, hint }) => (
  <div className="ds-empty">
    <div className="ds-empty-icon">
      <Icon icon={icon} />
    </div>
    <div className="ds-empty-title">{title}</div>
    <div className="ds-empty-hint">{hint}</div>
  </div>
);

const Dashboard = () => {
  const AppService = useAppServices();
  const { companyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [forms, setForms] = useState([]);
  const [locationCreaters, setLocationCreaters] = useState([]);
  const [locationIntiaters, setLocationIntiaters] = useState([]);
  const [timeRange, setTimeRange] = useState("30d");

  const getDashboardData = async () => {
    try {
      const { response } = await AppService.webhook.getdashboard_data({
        query: `company_id=${companyId}`,
      });
      if (response) {
        setLocations(response.locations || []);
        setUsers(response.users || []);
        setSupportTickets(response.supportTickets || []);
        setForms(response.forms || []);
        setLocationCreaters(response.locationCreaters || []);
        setLocationIntiaters(response.locationIntiaters || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDashboardData();
  }, []);

  const activeLocations = useMemo(
    () => locations.filter(isActiveLocation).length,
    [locations]
  );
  const inactiveLocations = locations.length - activeLocations;

  const openTickets = useMemo(
    () =>
      supportTickets.filter((t) => {
        const s = (t.status || "").toLowerCase();
        return !(
          s === "resolved" ||
          s === "closed" ||
          s === "completed" ||
          s === "cancelled" ||
          s === "canceled"
        );
      }).length,
    [supportTickets]
  );

  const activeTemplates = useMemo(
    () => locationCreaters.filter((t) => t.isActive).length,
    [locationCreaters]
  );

  const periodBounds = useMemo(() => {
    const now = new Date();
    let curr = null;
    let prev = null;
    if (timeRange === "30d") {
      curr = new Date(now);
      curr.setDate(now.getDate() - 30);
      prev = new Date(now);
      prev.setDate(now.getDate() - 60);
    } else if (timeRange === "3m") {
      curr = new Date(now);
      curr.setMonth(now.getMonth() - 3);
      prev = new Date(now);
      prev.setMonth(now.getMonth() - 6);
    } else if (timeRange === "6m") {
      curr = new Date(now);
      curr.setMonth(now.getMonth() - 6);
      prev = new Date(now);
      prev.setMonth(now.getMonth() - 12);
    }
    return { currStart: curr, prevStart: prev, end: now };
  }, [timeRange]);

  const mkDelta = (data, dateField, pred = () => true) => {
    const { currStart, prevStart, end } = periodBounds;
    if (!currStart) return null;
    const c = countInRange(data, currStart, end, dateField, pred);
    const p = countInRange(data, prevStart, currStart, dateField, pred);
    return computeDelta(c, p);
  };

  const primaryKpis = useMemo(
    () => [
      {
        label: "Total Users",
        value: users.length,
        delta: mkDelta(users, "dateAdded"),
        icon: "solar:users-group-rounded-bold-duotone",
        tone: "primary",
      },
      {
        label: "Total Locations",
        value: locations.length,
        delta: mkDelta(locations, "dateAdded"),
        icon: "solar:buildings-3-bold-duotone",
        tone: "violet",
      },
      {
        label: "Active SaaS",
        value: activeLocations,
        delta: mkDelta(locations, "dateAdded", isActiveLocation),
        icon: "solar:shield-check-bold-duotone",
        tone: "success",
      },
      {
        label: "Non-Active SaaS",
        value: inactiveLocations,
        delta: mkDelta(locations, "dateAdded", (l) => !isActiveLocation(l)),
        icon: "solar:shield-cross-bold-duotone",
        tone: "danger",
      },
    ],
    [users, locations, activeLocations, inactiveLocations, periodBounds]
  );

  const secondaryKpis = useMemo(
    () => [
      {
        label: "Support Tickets",
        value: supportTickets.length,
        subtext: `${openTickets} open`,
        delta: mkDelta(supportTickets, "createdAt"),
        icon: "solar:chat-round-dots-bold-duotone",
        tone: "indigo",
      },
      {
        label: "Custom Value Forms",
        value: forms.length,
        delta: mkDelta(forms, "createdAt"),
        icon: "solar:clipboard-text-bold-duotone",
        tone: "teal",
      },
      {
        label: "Location Templates",
        value: locationCreaters.length,
        subtext: `${activeTemplates} active`,
        delta: mkDelta(locationCreaters, "createdAt"),
        icon: "solar:map-point-add-bold-duotone",
        tone: "amber",
      },
      {
        label: "Location Initiators",
        value: locationIntiaters.length,
        delta: mkDelta(locationIntiaters, "createdAt"),
        icon: "solar:routing-2-bold-duotone",
        tone: "rose",
      },
    ],
    [
      supportTickets,
      forms,
      locationCreaters,
      locationIntiaters,
      openTickets,
      activeTemplates,
      periodBounds,
    ]
  );

  const filterByTimeRange = (data, dateField = "dateAdded") => {
    if (timeRange === "all") return data;
    const { currStart, end } = periodBounds;
    return data.filter((item) => {
      if (!item[dateField]) return false;
      const d = new Date(item[dateField]);
      return !isNaN(d) && d >= currStart && d <= end;
    });
  };

  const groupByMonth = (data, dateField = "dateAdded") => {
    const counts = {};
    data.forEach((item) => {
      if (!item[dateField]) return;
      const d = new Date(item[dateField]);
      if (isNaN(d)) return;
      const key = `${d.toLocaleString("default", {
        month: "short",
      })} ${d.getFullYear()}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  };

  const userCounts = groupByMonth(filterByTimeRange(users, "dateAdded"), "dateAdded");
  const locationCounts = groupByMonth(filterByTimeRange(locations, "dateAdded"), "dateAdded");
  const months = Array.from(
    new Set([...Object.keys(userCounts), ...Object.keys(locationCounts)])
  ).sort((a, b) => {
    const [ma, ya] = a.split(" ");
    const [mb, yb] = b.split(" ");
    return new Date(`${ma} 1, ${ya}`) - new Date(`${mb} 1, ${yb}`);
  });

  const growthOptions = {
    chart: { type: "area", toolbar: { show: false }, fontFamily: "inherit" },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05 },
    },
    xaxis: {
      categories: months,
      labels: { style: { colors: "#64748b", fontSize: "12px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: "#64748b", fontSize: "12px" } } },
    grid: { borderColor: "#eef1f6", strokeDashArray: 4 },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontSize: "13px",
      markers: { width: 8, height: 8, radius: 4 },
    },
    colors: ["#2563eb", "#10b981"],
    tooltip: { theme: "light" },
  };
  const growthSeries = [
    { name: "Users", data: months.map((m) => userCounts[m] || 0) },
    { name: "Locations", data: months.map((m) => locationCounts[m] || 0) },
  ];

  const subStatus = useMemo(() => {
    const out = { Active: 0, Trial: 0, Cancelled: 0, Inactive: 0 };
    locations.forEach((l) => {
      const sm = (l.saasMode || "").toLowerCase();
      const ss = (l.subscriptionStatus || "").toLowerCase();
      if (sm === "active" && ss === "active") out.Active++;
      else if (ss === "trial" || ss === "trialing") out.Trial++;
      else if (ss === "cancelled" || ss === "canceled") out.Cancelled++;
      else out.Inactive++;
    });
    return out;
  }, [locations]);

  const subStatusOptions = {
    chart: { type: "donut", fontFamily: "inherit" },
    labels: Object.keys(subStatus),
    legend: {
      position: "bottom",
      fontSize: "13px",
      markers: { width: 8, height: 8, radius: 4 },
    },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            name: { fontSize: "13px", color: "#64748b" },
            value: { fontSize: "22px", fontWeight: 700, color: "#0f172a" },
            total: {
              show: true,
              label: "Total",
              fontSize: "13px",
              color: "#64748b",
            },
          },
        },
      },
    },
    colors: ["#10b981", "#f59e0b", "#ef4444", "#94a3b8"],
    stroke: { width: 0 },
  };

  const roleCounts = useMemo(() => {
    return users.reduce((acc, user) => {
      const role = user.ghl_role || "Unknown";
      acc[role] = (acc[role] || 0) + 1;
      return acc;
    }, {});
  }, [users]);
  const roleLabels = Object.keys(roleCounts);
  const roleData = Object.values(roleCounts);
  const roleOptions = {
    chart: { type: "donut", fontFamily: "inherit" },
    labels: roleLabels,
    legend: {
      position: "bottom",
      fontSize: "13px",
      markers: { width: 8, height: 8, radius: 4 },
    },
    dataLabels: { enabled: false },
    plotOptions: { pie: { donut: { size: "72%" } } },
    colors: ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"],
    stroke: { width: 0 },
  };

  const ticketStatusBreakdown = useMemo(() => {
    const out = {};
    supportTickets.forEach((t) => {
      const s = (t.status || "Pending").trim() || "Pending";
      out[s] = (out[s] || 0) + 1;
    });
    return out;
  }, [supportTickets]);

  const ticketStatusOptions = {
    chart: { type: "donut", fontFamily: "inherit" },
    labels: Object.keys(ticketStatusBreakdown),
    legend: {
      position: "bottom",
      fontSize: "13px",
      markers: { width: 8, height: 8, radius: 4 },
    },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            name: { fontSize: "13px", color: "#64748b" },
            value: { fontSize: "22px", fontWeight: 700, color: "#0f172a" },
            total: {
              show: true,
              label: "Total",
              fontSize: "13px",
              color: "#64748b",
            },
          },
        },
      },
    },
    colors: ["#f59e0b", "#10b981", "#3b82f6", "#ef4444", "#94a3b8", "#8b5cf6"],
    stroke: { width: 0 },
  };

  const userLocationLookup = useMemo(() => {
    const map = new Map();
    locations.forEach((l) => {
      if (l.id) map.set(l.id, l.name || l.loc_name || "Unnamed");
    });
    return map;
  }, [locations]);

  const recentLocations = useMemo(
    () =>
      [...locations]
        .filter((l) => l.dateAdded)
        .sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded))
        .slice(0, 6),
    [locations]
  );

  const recentUsers = useMemo(
    () =>
      [...users]
        .filter((u) => u.dateAdded || u.createdAt)
        .sort(
          (a, b) =>
            new Date(b.dateAdded || b.createdAt) -
            new Date(a.dateAdded || a.createdAt)
        )
        .slice(0, 7),
    [users]
  );

  const recentTickets = useMemo(
    () => [...supportTickets].slice(0, 6),
    [supportTickets]
  );

  const recentForms = useMemo(
    () => [...forms].slice(0, 6),
    [forms]
  );

  const recentTemplates = useMemo(
    () => [...locationCreaters].slice(0, 6),
    [locationCreaters]
  );

  const recentIntiaters = useMemo(
    () => [...locationIntiaters].slice(0, 6),
    [locationIntiaters]
  );

  const quickActions = [
    {
      label: "Create Location",
      icon: "solar:map-point-add-bold-duotone",
      to: `/${companyId}/location-creator`,
      tone: "blue",
    },
    {
      label: "Initiate Location",
      icon: "solar:routing-2-bold-duotone",
      to: `/${companyId}/location_intiater`,
      tone: "violet",
    },
    {
      label: "Customize Menu",
      icon: "solar:layers-minimalistic-bold-duotone",
      to: `/${companyId}/menu_customization`,
      tone: "amber",
    },
    {
      label: "Theme Builder",
      icon: "solar:palette-bold-duotone",
      to: `/${companyId}/theme-builder`,
      tone: "rose",
    },
    {
      label: "Knowledgebase",
      icon: "solar:book-2-bold-duotone",
      to: `/${companyId}/knowledgebase`,
      tone: "teal",
    },
    {
      label: "Support",
      icon: "solar:chat-round-dots-bold-duotone",
      to: `/${companyId}/support`,
      tone: "indigo",
    },
  ];

  const renderKpiCard = (kpi, i) => (
    <div className="col-12 col-sm-6 col-xl-3" key={i}>
      <div className="kpi-card">
        <div className={`kpi-icon kpi-icon-${kpi.tone}`}>
          <Icon icon={kpi.icon} />
        </div>
        <div className="kpi-body">
          <div className="kpi-label">{kpi.label}</div>
          <div className="kpi-value-row">
            {loading ? (
              <span className="kpi-skel" />
            ) : (
              <span className="kpi-value">{kpi.value}</span>
            )}
            {!loading && kpi.delta && kpi.delta.dir !== "flat" && (
              <span className={`kpi-delta kpi-delta-${kpi.delta.dir}`}>
                <Icon
                  icon={
                    kpi.delta.dir === "up"
                      ? "mdi:arrow-up"
                      : "mdi:arrow-down"
                  }
                />
                {kpi.delta.value}%
              </span>
            )}
          </div>
          {kpi.subtext && !loading && (
            <div className="kpi-subtext">{kpi.subtext}</div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard-page container-fluid px-4 py-3">
      <style>{`
        .dashboard-page { color: #0f172a; }
        .dashboard-page .card-title {
          font-size: 15px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 2px;
          line-height: 1.3;
        }
        .dashboard-page .card-sub {
          font-size: 12.5px;
          color: #64748b;
          line-height: 1.3;
        }
        .kpi-card {
          background: #fff;
          border-radius: 14px;
          padding: 16px;
          border: 1px solid #eef1f6;
          display: flex;
          align-items: center;
          gap: 14px;
          height: 100%;
          transition: box-shadow .15s ease, transform .15s ease, border-color .15s ease;
        }
        .kpi-card:hover {
          box-shadow: 0 6px 18px -10px rgba(15,23,42,.18);
          transform: translateY(-1px);
          border-color: #e2e8f0;
        }
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          flex-shrink: 0;
        }
        .kpi-icon-primary { background: #eff6ff; color: #2563eb; }
        .kpi-icon-violet  { background: #f5f3ff; color: #7c3aed; }
        .kpi-icon-success { background: #ecfdf5; color: #10b981; }
        .kpi-icon-danger  { background: #fef2f2; color: #ef4444; }
        .kpi-icon-amber   { background: #fffbeb; color: #d97706; }
        .kpi-icon-teal    { background: #f0fdfa; color: #0d9488; }
        .kpi-icon-rose    { background: #fff1f2; color: #e11d48; }
        .kpi-icon-indigo  { background: #eef2ff; color: #4f46e5; }
        .kpi-body { flex: 1; min-width: 0; }
        .kpi-label {
          font-size: 12.5px;
          color: #64748b;
          font-weight: 500;
          margin-bottom: 4px;
        }
        .kpi-value-row {
          display: flex;
          align-items: baseline;
          gap: 8px;
          flex-wrap: wrap;
        }
        .kpi-value {
          font-size: 24px;
          font-weight: 700;
          color: #0f172a;
          line-height: 1;
        }
        .kpi-subtext {
          font-size: 11.5px;
          color: #64748b;
          margin-top: 3px;
          font-weight: 500;
        }
        .kpi-skel {
          display: inline-block;
          width: 56px;
          height: 22px;
          background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
          background-size: 200% 100%;
          animation: ds-skel 1.4s linear infinite;
          border-radius: 4px;
        }
        @keyframes ds-skel {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .kpi-delta {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          font-size: 11.5px;
          font-weight: 600;
          padding: 2px 7px;
          border-radius: 999px;
          line-height: 1.2;
        }
        .kpi-delta-up   { background: #ecfdf5; color: #059669; }
        .kpi-delta-down { background: #fef2f2; color: #dc2626; }

        .quick-actions {
          background: #fff;
          border-radius: 14px;
          border: 1px solid #eef1f6;
          padding: 16px;
        }
        .qa-head { margin-bottom: 14px; }
        .qa-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 10px;
        }
        .qa-tile {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          border-radius: 10px;
          background: #f8fafc;
          border: 1px solid #eef1f6;
          text-decoration: none;
          color: #0f172a;
          transition: background-color .15s ease, border-color .15s ease, transform .15s ease, box-shadow .15s ease;
        }
        .qa-tile:hover {
          background: #fff;
          border-color: #cbd5e1;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -8px rgba(15,23,42,.15);
          color: #0f172a;
        }
        .qa-icon {
          width: 36px;
          height: 36px;
          border-radius: 9px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .qa-tile-blue   .qa-icon { background: #eff6ff; color: #2563eb; }
        .qa-tile-violet .qa-icon { background: #f5f3ff; color: #7c3aed; }
        .qa-tile-amber  .qa-icon { background: #fffbeb; color: #d97706; }
        .qa-tile-rose   .qa-icon { background: #fff1f2; color: #e11d48; }
        .qa-tile-teal   .qa-icon { background: #f0fdfa; color: #0d9488; }
        .qa-tile-indigo .qa-icon { background: #eef2ff; color: #4f46e5; }
        .qa-label { flex: 1; font-size: 13.5px; font-weight: 600; }
        .qa-arrow {
          font-size: 16px;
          color: #94a3b8;
          opacity: 0;
          transition: opacity .15s ease, transform .15s ease;
        }
        .qa-tile:hover .qa-arrow {
          opacity: 1;
          transform: translate(2px, -2px);
        }

        .ds-card {
          background: #fff;
          border-radius: 14px;
          border: 1px solid #eef1f6;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .ds-card-head { padding: 16px 18px 8px; }
        .ds-card-body { padding: 8px 18px 16px; flex: 1; }
        .ds-card-body.p-0 { padding: 0; }

        .ds-table-wrap { overflow-x: auto; }
        .ds-table {
          width: 100%;
          font-size: 13px;
          border-collapse: collapse;
          margin: 0;
        }
        .ds-table thead th {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          font-weight: 600;
          padding: 10px 18px;
          background: #f8fafc;
          border-bottom: 1px solid #eef1f6;
          border-top: 1px solid #eef1f6;
          text-align: left;
          white-space: nowrap;
        }
        .ds-table tbody td {
          padding: 12px 18px;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
          color: #0f172a;
        }
        .ds-table tbody tr:last-child td { border-bottom: 0; }
        .ds-table tbody tr:hover td { background: #fafbfc; }
        .ds-table .user-sub {
          font-size: 11.5px;
          color: #64748b;
          font-weight: 400;
          margin-top: 1px;
        }
        .ds-table .truncate-cell {
          max-width: 260px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .avatar {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          flex-shrink: 0;
          letter-spacing: 0.02em;
        }
        .avatar-primary { background: #eff6ff; color: #2563eb; }
        .avatar-violet  { background: #f5f3ff; color: #7c3aed; }
        .avatar-indigo  { background: #eef2ff; color: #4f46e5; }
        .avatar-teal    { background: #f0fdfa; color: #0d9488; }
        .avatar-amber   { background: #fffbeb; color: #d97706; }
        .avatar-rose    { background: #fff1f2; color: #e11d48; }

        .badge-pill {
          display: inline-flex;
          align-items: center;
          padding: 3px 9px;
          border-radius: 999px;
          font-size: 11.5px;
          font-weight: 600;
          text-transform: capitalize;
          white-space: nowrap;
          line-height: 1.4;
        }
        .bp-success { background: #ecfdf5; color: #047857; }
        .bp-warning { background: #fffbeb; color: #b45309; }
        .bp-danger  { background: #fef2f2; color: #b91c1c; }
        .bp-info    { background: #eef2ff; color: #4338ca; }
        .bp-muted   { background: #f1f5f9; color: #475569; }

        .ds-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 32px 16px;
          text-align: center;
        }
        .ds-empty-icon {
          font-size: 40px;
          color: #94a3b8;
          margin-bottom: 8px;
        }
        .ds-empty-title {
          font-size: 14px;
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 4px;
        }
        .ds-empty-hint {
          font-size: 12.5px;
          color: #64748b;
          max-width: 320px;
        }

        .ds-period-select {
          padding: 6px 30px 6px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 13px;
          color: #0f172a;
          background-color: #fff;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 10px center;
          appearance: none;
          -webkit-appearance: none;
          cursor: pointer;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .ds-period-select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37,99,235,.15);
        }
      `}</style>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div>
          <h3 className="mb-1 fw-bold">Dashboard</h3>
          <div className="text-secondary-light small">
            Overview of users, locations, tickets and SaaS health
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="small text-secondary-light">Period</span>
          <select
            className="ds-period-select"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="30d">Last 30 Days</option>
            <option value="3m">Last 3 Months</option>
            <option value="6m">Last 6 Months</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {primaryKpis.map(renderKpiCard)}
      </div>

      <div className="row g-3 mb-3">
        {secondaryKpis.map(renderKpiCard)}
      </div>

      <div className="quick-actions mb-3">
        <div className="qa-head">
          <div className="card-title">Quick actions</div>
          <div className="card-sub">Jump straight to common tasks</div>
        </div>
        <div className="qa-grid">
          {quickActions.map((a, i) => (
            <Link key={i} to={a.to} className={`qa-tile qa-tile-${a.tone}`}>
              <div className="qa-icon">
                <Icon icon={a.icon} />
              </div>
              <div className="qa-label">{a.label}</div>
              <Icon icon="mdi:arrow-top-right" className="qa-arrow" />
            </Link>
          ))}
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-8">
          <div className="ds-card h-100">
            <div className="ds-card-head">
              <div className="card-title">Growth Over Time</div>
              <div className="card-sub">New users & locations per month</div>
            </div>
            <div className="ds-card-body">
              {months.length === 0 ? (
                <EmptyState
                  icon="solar:chart-2-bold-duotone"
                  title="No growth data yet"
                  hint="Once users and locations are added you'll see trends here."
                />
              ) : (
                <ReactApexChart
                  options={growthOptions}
                  series={growthSeries}
                  type="area"
                  height={340}
                />
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-4">
          <div className="ds-card h-100">
            <div className="ds-card-head">
              <div className="card-title">Subscription Status</div>
              <div className="card-sub">Location subscription breakdown</div>
            </div>
            <div className="ds-card-body">
              {locations.length === 0 ? (
                <EmptyState
                  icon="solar:buildings-3-bold-duotone"
                  title="No locations"
                  hint="Subscription status will appear once locations are added."
                />
              ) : (
                <ReactApexChart
                  options={subStatusOptions}
                  series={Object.values(subStatus)}
                  type="donut"
                  height={340}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-4">
          <div className="ds-card h-100">
            <div className="ds-card-head">
              <div className="card-title">Support Ticket Status</div>
              <div className="card-sub">Open vs resolved breakdown</div>
            </div>
            <div className="ds-card-body">
              {supportTickets.length === 0 ? (
                <EmptyState
                  icon="solar:chat-round-dots-bold-duotone"
                  title="No tickets"
                  hint="Ticket status will appear once tickets are created."
                />
              ) : (
                <ReactApexChart
                  options={ticketStatusOptions}
                  series={Object.values(ticketStatusBreakdown)}
                  type="donut"
                  height={340}
                />
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-8">
          <div className="ds-card h-100">
            <div className="ds-card-head">
              <div className="card-title">Recent Support Tickets</div>
              <div className="card-sub">Latest tickets submitted</div>
            </div>
            <div className="ds-card-body p-0">
              {recentTickets.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    icon="solar:chat-round-dots-bold-duotone"
                    title="No support tickets yet"
                    hint="Tickets users open will show up here."
                  />
                </div>
              ) : (
                <div className="ds-table-wrap">
                  <table className="ds-table">
                    <thead>
                      <tr>
                        <th>Subject</th>
                        <th>User</th>
                        <th>Status</th>
                        <th>Priority</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentTickets.map((t, i) => (
                        <tr key={t._id || t.ticket_id || i}>
                          <td className="truncate-cell">
                            <div className="fw-semibold">
                              {t.subject || "Untitled"}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="avatar avatar-indigo">
                                {getInitials(t.userName || t.email)}
                              </div>
                              <div>
                                <div className="fw-semibold">
                                  {t.userName || "Unknown"}
                                </div>
                                {t.email && (
                                  <div className="user-sub">{t.email}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`badge-pill ${ticketStatusTone(
                                t.status
                              )}`}
                            >
                              {t.status || "Pending"}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`badge-pill ${priorityTone(
                                t.priority
                              )}`}
                            >
                              {t.priority || "medium"}
                            </span>
                          </td>
                          <td className="text-secondary-light">
                            {fmtDate(t.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-4">
          <div className="ds-card h-100">
            <div className="ds-card-head">
              <div className="card-title">User Roles</div>
              <div className="card-sub">Distribution by GHL role</div>
            </div>
            <div className="ds-card-body">
              {roleLabels.length === 0 ? (
                <EmptyState
                  icon="solar:users-group-rounded-bold-duotone"
                  title="No users"
                  hint="Role distribution will appear once users are added."
                />
              ) : (
                <ReactApexChart
                  options={roleOptions}
                  series={roleData}
                  type="donut"
                  height={340}
                />
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-8">
          <div className="ds-card h-100">
            <div className="ds-card-head">
              <div className="card-title">Recent Locations</div>
              <div className="card-sub">Latest locations added</div>
            </div>
            <div className="ds-card-body p-0">
              {recentLocations.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    icon="solar:map-point-add-bold-duotone"
                    title="No locations yet"
                    hint="Locations you add will show up here."
                  />
                </div>
              ) : (
                <div className="ds-table-wrap">
                  <table className="ds-table">
                    <thead>
                      <tr>
                        <th>Location</th>
                        <th>SaaS Mode</th>
                        <th>Subscription</th>
                        <th>Added</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentLocations.map((l, i) => (
                        <tr key={l.id || l._id || i}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="avatar avatar-violet">
                                {getInitials(l.name || l.loc_name)}
                              </div>
                              <div className="fw-semibold">
                                {l.name || l.loc_name || "Unnamed"}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`badge-pill ${
                                l.saasMode === "active"
                                  ? "bp-success"
                                  : "bp-muted"
                              }`}
                            >
                              {l.saasMode || "—"}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`badge-pill ${subscriptionTone(
                                l.subscriptionStatus
                              )}`}
                            >
                              {l.subscriptionStatus || "—"}
                            </span>
                          </td>
                          <td className="text-secondary-light">
                            {fmtDate(l.dateAdded)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-6">
          <div className="ds-card h-100">
            <div className="ds-card-head">
              <div className="card-title">Recent Custom Value Forms</div>
              <div className="card-sub">Latest forms created</div>
            </div>
            <div className="ds-card-body p-0">
              {recentForms.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    icon="solar:clipboard-text-bold-duotone"
                    title="No forms yet"
                    hint="Forms you create will show up here."
                  />
                </div>
              ) : (
                <div className="ds-table-wrap">
                  <table className="ds-table">
                    <thead>
                      <tr>
                        <th>Form</th>
                        <th>Location</th>
                        <th>Fields</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentForms.map((f, i) => (
                        <tr key={f._id || i}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="avatar avatar-teal">
                                {getInitials(f.name)}
                              </div>
                              <div className="fw-semibold truncate-cell">
                                {f.name || "Untitled"}
                              </div>
                            </div>
                          </td>
                          <td className="text-secondary-light">
                            {userLocationLookup.get(f.locationId) || "—"}
                          </td>
                          <td>
                            <span className="badge-pill bp-info">
                              {f.fields?.length || 0} fields
                            </span>
                          </td>
                          <td className="text-secondary-light">
                            {fmtDate(f.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="ds-card h-100">
            <div className="ds-card-head">
              <div className="card-title">Recent Location Initiators</div>
              <div className="card-sub">Latest location initiations</div>
            </div>
            <div className="ds-card-body p-0">
              {recentIntiaters.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    icon="solar:routing-2-bold-duotone"
                    title="No initiations yet"
                    hint="Location initiations will show up here."
                  />
                </div>
              ) : (
                <div className="ds-table-wrap">
                  <table className="ds-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Location</th>
                        <th>Status</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentIntiaters.map((it, i) => {
                        const locName =
                          typeof it.locationName === "object"
                            ? it.locationName?.name || it.locationName?.label
                            : it.locationName;
                        return (
                          <tr key={it._id || i}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div className="avatar avatar-rose">
                                  {getInitials(it.name)}
                                </div>
                                <div className="fw-semibold truncate-cell">
                                  {it.name || "Untitled"}
                                </div>
                              </div>
                            </td>
                            <td className="text-secondary-light">
                              {locName ||
                                userLocationLookup.get(it.locationId) ||
                                "—"}
                            </td>
                            <td>
                              <span
                                className={`badge-pill ${initiatorTone(
                                  it.status
                                )}`}
                              >
                                {it.status || "—"}
                              </span>
                            </td>
                            <td className="text-secondary-light">
                              {fmtDate(it.createdAt)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12">
          <div className="ds-card">
            <div className="ds-card-head">
              <div className="card-title">Recent Location Templates</div>
              <div className="card-sub">
                Templates created via Location Creator
              </div>
            </div>
            <div className="ds-card-body p-0">
              {recentTemplates.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    icon="solar:map-point-add-bold-duotone"
                    title="No templates yet"
                    hint="Location templates you create will show up here."
                  />
                </div>
              ) : (
                <div className="ds-table-wrap">
                  <table className="ds-table">
                    <thead>
                      <tr>
                        <th>Template</th>
                        <th>Status</th>
                        <th>Users</th>
                        <th>Custom Values</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentTemplates.map((t, i) => (
                        <tr key={t._id || i}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="avatar avatar-amber">
                                {getInitials(t.template_name)}
                              </div>
                              <div className="fw-semibold truncate-cell">
                                {t.template_name || "Untitled"}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`badge-pill ${
                                t.isActive ? "bp-success" : "bp-muted"
                              }`}
                            >
                              {t.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <span className="badge-pill bp-info">
                              {Array.isArray(t.user_data)
                                ? t.user_data.length
                                : 0}
                            </span>
                          </td>
                          <td>
                            <span className="badge-pill bp-info">
                              {Array.isArray(t.custom_values_data)
                                ? t.custom_values_data.length
                                : 0}
                            </span>
                          </td>
                          <td className="text-secondary-light">
                            {fmtDate(t.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12">
          <div className="ds-card">
            <div className="ds-card-head">
              <div className="card-title">Recent Users</div>
              <div className="card-sub">Latest users added</div>
            </div>
            <div className="ds-card-body p-0">
              {recentUsers.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    icon="solar:users-group-rounded-bold-duotone"
                    title="No users yet"
                    hint="Users you add will show up here."
                  />
                </div>
              ) : (
                <div className="ds-table-wrap">
                  <table className="ds-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Added</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentUsers.map((u, i) => {
                        const fullName = buildUserName(u);
                        return (
                          <tr key={u.id || u._id || i}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div className="avatar avatar-primary">
                                  {getInitials(fullName)}
                                </div>
                                <div>
                                  <div className="fw-semibold">{fullName}</div>
                                  {u.username && u.username !== fullName && (
                                    <div className="user-sub">
                                      @{u.username}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="text-secondary-light">
                              {u.email || "—"}
                            </td>
                            <td>
                              <span className="badge-pill bp-info">
                                {u.ghl_role || "Unknown"}
                              </span>
                            </td>
                            <td className="text-secondary-light">
                              {fmtDate(u.dateAdded || u.createdAt)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
